import { useState } from "react";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { platformClient, formatPlatformError } from "./platformApi";

export const PlatformLogin = ({ onLogin, notice = "" }) => {
    const [mode, setMode] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await platformClient.post("/auth/login", { email, password });
            onLogin(data);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const submitForgot = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/auth/forgot-password", { email });
            setForgotSent(true);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="platform-login"
            className="flex min-h-screen items-center justify-center bg-ink px-6"
        >
            <div className="w-full max-w-sm border border-white/10 bg-coal p-8">
                <div className="flex flex-col items-center">
                    <img
                        src="/logo/cbs-lockup-dark.svg"
                        alt="Clarke Business Solutions"
                        data-testid="login-logo-lockup"
                        className="w-40"
                    />
                    <p className="mt-4 text-[11px] uppercase tracking-[0.3em] text-ash">
                        {mode === "login" ? "Platform — Inloggen" : "Wachtwoord vergeten"}
                    </p>
                </div>
                {notice ? (
                    <p data-testid="idle-logout-notice" className="mt-6 border border-gold/40 bg-gold/10 p-4 text-center text-xs leading-relaxed text-gold">
                        {notice}
                    </p>
                ) : null}
                {mode === "forgot" ? (
                    forgotSent ? (
                        <div className="mt-8 space-y-6">
                            <p data-testid="forgot-success-message" className="text-sm leading-relaxed text-ash">
                                Als dit e-mailadres bij ons bekend is, ontvang je binnen enkele
                                minuten een e-mail met een herstel-link. De link is 30 minuten geldig.
                            </p>
                            <button
                                data-testid="forgot-back-button"
                                onClick={() => { setMode("login"); setForgotSent(false); }}
                                className="w-full border border-white/15 px-8 py-4 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                            >
                                Terug naar inloggen
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submitForgot} className="mt-8 space-y-6">
                            <p className="text-sm leading-relaxed text-ash">
                                Vul je e-mailadres in. We sturen je een link om een nieuw
                                wachtwoord in te stellen.
                            </p>
                            <label className="block">
                                <span className="text-[11px] uppercase tracking-[0.3em] text-ash">E-mail</span>
                                <input
                                    data-testid="forgot-email-input"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="username"
                                    className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                                />
                            </label>
                            {error ? (
                                <p data-testid="forgot-error" className="text-sm text-red-400">
                                    {error}
                                </p>
                            ) : null}
                            <button
                                data-testid="forgot-submit-button"
                                type="submit"
                                disabled={busy}
                                className="w-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                            >
                                {busy ? "Versturen…" : "Verstuur herstel-link"}
                            </button>
                            <button
                                data-testid="forgot-cancel-button"
                                type="button"
                                onClick={() => setMode("login")}
                                className="w-full text-center text-[11px] uppercase tracking-[0.25em] text-ash transition-colors hover:text-gold"
                            >
                                Terug naar inloggen
                            </button>
                        </form>
                    )
                ) : (
                    <form onSubmit={submit} className="mt-8 space-y-6">
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.3em] text-ash">E-mail</span>
                            <input
                                data-testid="platform-login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="username"
                                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                            />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-[0.3em] text-ash">
                                Wachtwoord
                            </span>
                            <PasswordInput
                                testid="platform-login-password-input"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-3 w-full border-b border-white/20 bg-transparent pb-3 text-base text-bone transition-colors focus:border-gold focus:outline-none"
                            />
                        </label>
                        {error ? (
                            <p data-testid="platform-login-error" className="text-sm text-red-400">
                                {error}
                            </p>
                        ) : null}
                        <button
                            data-testid="platform-login-submit-button"
                            type="submit"
                            disabled={busy}
                            className="w-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone disabled:opacity-60"
                        >
                            {busy ? "Inloggen…" : "Inloggen"}
                        </button>
                        <button
                            data-testid="forgot-password-link"
                            type="button"
                            onClick={() => { setMode("forgot"); setError(""); }}
                            className="w-full text-center text-[11px] uppercase tracking-[0.25em] text-ash transition-colors hover:text-gold"
                        >
                            Wachtwoord vergeten?
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};
